import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import axios from "axios";
import { Upload, Plus, Trash2, Download, X, Loader, Info, FileText } from "lucide-react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Config from "../config.js";
import "../styles/CreateCertificate.css";

/** Download / export basename slug (ASCII); API certificateType unchanged. */
const CERTIFICATE_DOWNLOAD_SLUG = {
  "Bedienerausweis für Hubarbeitsbühnen": "Unterweisung_Hubarbeitsbuehnen",
  "Sachkundenachweis TRGS 519": "Sachkundenachweis_TRGS519_ASI",
};

function formatValueToDdmmyy(value) {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const [, y, mo, d] = iso;
    return `${d}${mo}${y.slice(-2)}`;
  }
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})\b/.exec(s);
  if (de) {
    const [, d, mo, y] = de;
    return `${d.padStart(2, "0")}${mo.padStart(2, "0")}${y.slice(-2)}`;
  }
  const deShort = /^(\d{1,2})\.(\d{1,2})\.(\d{2})\b/.exec(s);
  if (deShort) {
    const [, d, mo, y] = deShort;
    return `${d.padStart(2, "0")}${mo.padStart(2, "0")}${y}`;
  }
  return null;
}

function currentDateDdmmyy() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = String(now.getFullYear()).slice(-2);
  return `${day}${month}${year}`;
}

function getDownloadDateDdmmyy(certificateType, rows, requiredFieldsMap) {
  const req = requiredFieldsMap[certificateType] || [];
  const tryFields = [];
  if (req.includes("prufungsdatum")) tryFields.push("prufungsdatum");
  if (req.includes("startdatum")) tryFields.push("startdatum");
  for (const field of tryFields) {
    for (const row of rows) {
      const v = formatValueToDdmmyy(row[field]);
      if (v) return v;
    }
  }
  return currentDateDdmmyy();
}

function getDownloadTypeSlug(certificateType) {
  if (CERTIFICATE_DOWNLOAD_SLUG[certificateType]) {
    return CERTIFICATE_DOWNLOAD_SLUG[certificateType];
  }
  return certificateType.replace(/ /g, "_");
}

const CreateCertificate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [certificateType, setCertificateType] = useState("");
  const [rows, setRows] = useState([{}]);
  const [errors, setErrors] = useState([]);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [isPrintableVersion, setIsPrintableVersion] = useState(false);
  const [includeBarcode, setIncludeBarcode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showValidityInfo, setShowValidityInfo] = useState(false);
  const fileInputRef = useRef(null);

  const certificateTypes = [
    "Namensschild",
    "Verschraubungsmonteur",
    "Flanschmonteur",
    "PSAgA",
    "Jährliche Schulung für Hubarbeitsbühnen",
    "Bedienerausweis für Hubarbeitsbühnen",
    "Anschlagen von Lasten",
    "Atemschutzgeräte",
    "Brandschutzbeauftragter",
    "SiGeKo",
    "SCC Document 16",
    "EHS Manager",
    "Sifa LEK 1-5",
    "Sifa LEK 6",
    "Sifa Nachweis",
    "Sifa Abschluss",
    "Sifa Lernbegleiter",
    "Sachkundenachweis TRGS 519",
  ];

  const requiredFields = {
    Namensschild: ["vorname", "nachname"],
    Flanschmonteur: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    Verschraubungsmonteur: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    PSAgA: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Jährliche Schulung für Hubarbeitsbühnen": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Bedienerausweis für Hubarbeitsbühnen": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Anschlagen von Lasten": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Atemschutzgeräte": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Brandschutzbeauftragter": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "ausgestelltAm"],
    SiGeKo: ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "ausgestelltAm"],
    "SCC Document 16": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "EHS Manager": ["vorname", "nachname", "gebDatum", "prufungsdatum", "prufungsort", "gultigBis", "ausgestelltAm", "prufer"],
    "Sifa LEK 1-5": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa LEK 6": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Nachweis": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Abschluss": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sifa Lernbegleiter": ["geschlecht", "vorname", "nachname", "gebDatum", "startdatum", "enddatum", "ausgestelltAm", "teilnehmerId"],
    "Sachkundenachweis TRGS 519": [
      "vorname", "nachname", "gebDatum", "geburtsort",
      "startdatum", "endPrufungsdatum", "prufungsort",
      "ausgestelltAm", "prufer",
    ],
  };

  const optionalFields = {
    "Sifa LEK 1-5": ["ersatzDatum"],
    "Sachkundenachweis TRGS 519": ["gultigBis", "teilnehmerNr"],
    // "Sifa LEK 6": ["branche"],
    // "Sifa Abschluss": ["branche"],
  };

  const fieldLabels = {
    vorname: t("createCertificate.fields.vorname"),
    nachname: t("createCertificate.fields.nachname"),
    gebDatum: t("createCertificate.fields.gebDatum"),
    prufungsdatum: t("createCertificate.fields.prufungsdatum"),
    prufungsort: t("createCertificate.fields.prufungsort"),
    gultigBis: t("createCertificate.fields.gultigBis"),
    ausgestelltAm: t("createCertificate.fields.ausgestelltAm"),
    prufer: t("createCertificate.fields.prufer"),
    geschlecht: t("createCertificate.fields.geschlecht"),
    startdatum: t("createCertificate.fields.startdatum"),
    enddatum: t("createCertificate.fields.enddatum"),
    endPrufungsdatum: t("createCertificate.fields.endPrufungsdatum"),
    branche: t("createCertificate.fields.branche"),
    teilnehmerId: t("createCertificate.fields.teilnehmerId"),
    ersatzDatum: t("createCertificate.fields.ersatzDatum"),
    geburtsort: t("createCertificate.fields.geburtsort"),
    bescheidDatum: t("createCertificate.fields.bescheidDatum"),
    aktenzeichen: t("createCertificate.fields.aktenzeichen"),
    teilnehmerNr: t("createCertificate.fields.teilnehmerNr"),
  };

  // Aliases must match normalizeText() output (lowercase, a-z0-9 only).
  const columnAliases = {
    vorname: ["vorname", "firstname", "name"],
    nachname: ["nachname", "lastname", "surname", "name"],
    gebDatum: ["gebdatum", "birthdate", "dateofbirth", "geburtsdatum"],
    geburtsort: ["geburtsort", "birthplace", "placeofbirth"],
    prufungsdatum: ["prufungsdatum", "examdate", "pruefungsdatum"],
    prufungsort: [
      "prufungsort", "examlocation", "pruefungsort",
      "kursort", "kursortstadt", "courselocation", "traininglocation",
    ],
    bescheidDatum: ["bescheiddatum", "accreditationdate", "anerkennungsdatum"],
    aktenzeichen: ["aktenzeichen", "filenumber", "referencenumber"],
    // Zertifikatsnr ← Teilnehmernummer from TRGS / course lists
    teilnehmerNr: [
      "teilnehmernr", "teilnehmernummer",
      "participantnumber", "tnnr", "tnnummer",
      "zertifikatsnr", "zertifikatsnummer", "zertifikatnummer", "certificatenumber", "certnumber",
    ],
    gultigBis: ["gultigbis", "validuntil", "gueltigbis"],
    ausgestelltAm: ["ausgestelltam", "issuedon", "ausgestelltdatum", "issuedate"],
    prufer: [
      "prufer", "examiner", "pruefer",
      "lehrgangsleitung", "coursemanagement", "coursedirector", "courselead",
    ],
    geschlecht: ["geschlecht", "gender", "sex"],
    // Note: "kursdatum" is handled separately — it must NOT overwrite Startdatum/Enddatum columns
    startdatum: ["startdatum", "startdate", "kursbeginn", "coursestart"],
    enddatum: ["enddatum", "enddate", "kursende", "courseend"],
    // Combined End / Exam date (TRGS 519) — same value for both backend fields
    endPrufungsdatum: [
      "endprufungsdatum", "endexamdate", "enddateexamdate",
      "enddatum", "enddate", "kursende", "courseend",
      "prufungsdatum", "pruefungsdatum", "examdate",
    ],
    branche: ["branche", "branch", "industry", "sector"],
    teilnehmerId: [
      "teilnehmerid",
      "participantid",
      "tnid",
    ],
    ersatzDatum: [
      "ersatzdatum",
      "ersatz-datum",
      "replacement-date",
      "replacementdate",
      "ersatz datum",
      "replacement date (optional)",
      "ersatzdatum (optional)",
      "ersatz-datum (optional)",
      "replacement date",
      "replacement-date (optional)",
      "replacementdate (optional)",
      "Ersatzdatum (optional)",
      "Replacement Date (Optional)"
    ],
  };

  const dateFields = [
    "gebDatum", "prufungsdatum", "gultigBis", "ausgestelltAm",
    "startdatum", "enddatum", "endPrufungsdatum", "ersatzDatum", "bescheidDatum",
  ];

  // Function to generate Teilnehmer-ID
  const generateTeilnehmerId = (startDate, participantNumber) => {
    if (!startDate) return "";
    
    try {
      // Parse the start date (assuming DD.MM.YYYY format)
      const [day, month, year] = startDate.split('.');
      const shortYear = year.slice(-2); // Get last 2 digits of year
      const formattedDate = `${shortYear}${month}${day}`;
      
      // Format participant number with leading zeros (3 digits)
      const formattedNumber = participantNumber.toString().padStart(3, '0');
      
      return `T${formattedDate}-${formattedNumber}`;
    } catch (error) {
      console.error("Error generating Teilnehmer-ID:", error);
      return "";
    }
  };

  const calculateValidUntil = (examDate, certificateType) => {
    if (!examDate) return "";
    
    try {
      // Parse the exam date (assuming DD.MM.YYYY or DD.MM.YY format)
      const [day, month, year] = examDate.split('.');
      
      // Handle 2-digit years: assume 20xx for years 00-99
      let fullYear = parseInt(year);
      if (year.length === 2) {
        fullYear = 2000 + fullYear;
      }
      
      const examDateObj = new Date(fullYear, month - 1, day); // month is 0-indexed
      
      // Calculate validity period
      let validityYears = 1;
      if (certificateType === "SCC Document 16") validityYears = 5;
      else if (certificateType === "Sachkundenachweis TRGS 519") validityYears = 6;

      // Add the validity period
      const validUntilDate = new Date(examDateObj);
      validUntilDate.setFullYear(validUntilDate.getFullYear() + validityYears);

      // ASI: keep calendar day (matches backend add_years_keep_day). Others: day before anniversary.
      if (certificateType !== "Sachkundenachweis TRGS 519") {
        validUntilDate.setDate(validUntilDate.getDate() - 1);
      }
      
      // Format back to DD.MM.YYYY
      const formattedDay = validUntilDate.getDate().toString().padStart(2, '0');
      const formattedMonth = (validUntilDate.getMonth() + 1).toString().padStart(2, '0');
      const formattedYear = validUntilDate.getFullYear();
      
      return `${formattedDay}.${formattedMonth}.${formattedYear}`;
    } catch (error) {
      console.error("Error calculating valid until date:", error);
      return "";
    }
  };

  // Function to get validity period information
  const getValidityInfo = (certType) => {
    if (certType === "SCC Document 16") {
      return {
        period: "5 years",
        message: "SCC Document 16 certificates are valid for 5 years."
      };
    }
    if (certType === "Sachkundenachweis TRGS 519") {
      return {
        period: "6 years",
        message: "ASI Sachkundenachweis TRGS 519 is valid for 6 years from the issue date."
      };
    }
    const reqFields = requiredFields[certType] || [];
    const optFields = optionalFields[certType] || [];
    if (reqFields.includes("gultigBis") || optFields.includes("gultigBis")) {
      return {
        period: "1 year",
        message: "This certificate is generally valid for 1 year."
      };
    }
    return null;
  };

  const normalizeText = (text) => {
    if (!text || typeof text !== "string") return "";
    return text
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]/g, "");
  };

  const isValidName = (name) => {
    if (!name || typeof name !== "string") return false;
    const trimmedName = name.trim();
    if (trimmedName === "") return false;
    return /^[A-Za-zÀ-ÿ\s'-]+$/.test(trimmedName);
  };

  const isDDMMYYYYFormat = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return false;
    const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    if (!regex.test(dateStr)) return false;
    const [, day, month, year] = dateStr.match(regex);
    const date = new Date(`${year}-${month}-${day}`);
    return !isNaN(date.getTime()) && date.getDate() === parseInt(day) && (date.getMonth() + 1) === parseInt(month);
  };

  const normalizeGenderValue = (value) => {
    if (!value) return "";
    const v = value.toString().trim().toLowerCase();
    // Map common variants to Herr/Frau; default to Divers if unclear
    const maleTokens = [
      "m",
      "mann",
      "maennlich",
      "männlich",
      "male",
      "herr",
      "man",
    ];
    const femaleTokens = [
      "w",
      "weiblich",
      "female",
      "frau",
      "woman",
      "lady",
    ];
    if (maleTokens.includes(v)) return "Herr";
    if (femaleTokens.includes(v)) return "Frau";
    if (v === "divers" || v === "d" || v === "other" || v === "diverse") return "Divers";
    return "Divers"; // fallback
  };

  const formatDateObj = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return null;
    // SheetJS Date values are timezone-shifted; prefer cell.w via excelCellToValue.
    // Local getters match the calendar day in the user's timezone.
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    if (year < 1900 || year > 2100) return null;
    return `${day}.${month}.${year}`;
  };

  /** Prefer Excel display text (cell.w) so TZ never shifts the calendar day. */
  const excelCellToValue = (cell) => {
    if (!cell) return "";
    const formatted = cell.w != null ? String(cell.w).trim() : "";
    if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(formatted)) {
      const parts = formatted.split(".");
      if (parts[2].length === 2) parts[2] = `20${parts[2]}`;
      return `${parts[0].padStart(2, "0")}.${parts[1].padStart(2, "0")}.${parts[2]}`;
    }
    return cell.v == null ? "" : cell.v;
  };

  const convertDateToDDMMYYYY = (raw) => {
    if (raw == null || raw === "") return raw;
    if (raw instanceof Date) {
      return formatDateObj(raw) || "";
    }
    // Excel serial date (e.g. 46314 → 19.10.2026); use UTC to avoid TZ off-by-one
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 20000 && raw < 80000) {
      const date = new Date(Date.UTC(1899, 11, 30) + Math.round(raw) * 86400000);
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${day}.${month}.${year}`;
    }

    const dateStr = String(raw).trim();
    if (isDDMMYYYYFormat(dateStr)) return dateStr;

    // Numeric string that is an Excel serial
    if (/^\d{5}(\.0+)?$/.test(dateStr)) {
      return convertDateToDDMMYYYY(parseFloat(dateStr));
    }

    let date;
    const normalizedDateStr = dateStr.replace(/[-.]/g, "/");
    date = new Date(normalizedDateStr);

    if (isNaN(date.getTime())) {
      const parts = normalizedDateStr.split("/");
      if (parts.length === 3) {
        const [year, month, day] = parts;
        if (year.length === 4 && month.length <= 2 && day.length <= 2) {
          date = new Date(`${year}-${month}-${day}`);
        }
      }
    }

    return formatDateObj(date) || dateStr;
  };

  // Dynamic Loading Messages
  useEffect(() => {
    if (!isGenerating) {
      setLoadingMessage("");
      return;
    }

    const messages = includeBarcode
      ? [
          t("createCertificate.messages.loadingGenerating"),
          t("createCertificate.messages.loadingBlockchain"),
          t("createCertificate.messages.loadingHash"),
          t("createCertificate.messages.loadingBarcode"),
          t("createCertificate.messages.loadingFinalizing"),
        ]
      : [t("createCertificate.messages.loadingGenerating")];

    let currentIndex = 0;
    setLoadingMessage(messages[currentIndex]);

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % messages.length;
      setLoadingMessage(messages[currentIndex]);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [isGenerating, includeBarcode, t]);

  const handleCertificateTypeChange = (e) => {
    const newCertificateType = e.target.value;
    const oldCertificateType = certificateType;
    
    setCertificateType(newCertificateType);
    setErrors([]);
    setFile(null);
    setStatus("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Preserve common field values when switching certificate types
    if (oldCertificateType && rows.length > 0 && rows[0] && Object.keys(rows[0]).length > 0) {
      const oldRequiredFields = requiredFields[oldCertificateType] || [];
      const oldOptionalFields = optionalFields[oldCertificateType] || [];
      const oldAllFields = [...oldRequiredFields, ...oldOptionalFields];
      
      const newRequiredFields = requiredFields[newCertificateType] || [];
      const newOptionalFields = optionalFields[newCertificateType] || [];
      const newAllFields = [...newRequiredFields, ...newOptionalFields];
      
      // Find common fields between old and new certificate types
      const commonFields = oldAllFields.filter(field => newAllFields.includes(field));
      
      // Preserve values for common fields
      const preservedRows = rows.map(row => {
        const preservedRow = {};
        commonFields.forEach(field => {
          if (row[field] && row[field].trim() !== "") {
            preservedRow[field] = row[field];
          }
        });
        return preservedRow;
      });
      
      // If no common fields or no preserved data, start with empty row
      if (commonFields.length === 0 || preservedRows.every(row => Object.keys(row).length === 0)) {
        setRows([{}]);
      } else {
        setRows(preservedRows);
      }
    } else {
      setRows([{}]);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    
    // Auto-generate Teilnehmer-ID for Sifa certificates when startdatum changes
    if (field === "startdatum" && certificateType && certificateType.startsWith("Sifa")) {
      const participantNumber = index + 1; // Row index + 1 for participant number
      // Do not overwrite TN ID if it was imported from Excel
      if (!updatedRows[index]["teilnehmerId"] || updatedRows[index]["teilnehmerId"].toString().trim() === "") {
        const teilnehmerId = generateTeilnehmerId(value, participantNumber);
        updatedRows[index]["teilnehmerId"] = teilnehmerId;
      }
    }
    
    // Combined End/Exam date writes both backend fields
    if (field === "endPrufungsdatum") {
      updatedRows[index]["enddatum"] = value;
      updatedRows[index]["prufungsdatum"] = value;
    }

    // Auto-fill Gültig bis: ASI from issue date (6y); others from exam date
    if (certificateType === "Sachkundenachweis TRGS 519" && field === "ausgestelltAm") {
      const validUntil = calculateValidUntil(value, certificateType);
      if (validUntil) updatedRows[index]["gultigBis"] = validUntil;
    } else if (field === "prufungsdatum" && certificateType && certificateType !== "Sachkundenachweis TRGS 519") {
      const validUntil = calculateValidUntil(value, certificateType);
      if (validUntil) updatedRows[index]["gultigBis"] = validUntil;
    }
    
    setRows(updatedRows);

    const updatedErrors = errors.filter(
      (error) => !(error.row === index && error.field === field)
    );
    setErrors(updatedErrors);
  };

  const applyFieldToAllRows = (field, value) => {
    const updatedRows = rows.map((row) => {
      const next = { ...row, [field]: value };
      if (field === "endPrufungsdatum") {
        next.enddatum = value;
        next.prufungsdatum = value;
      }
      return next;
    });
    setRows(updatedRows);
    validateRows(updatedRows);
    setStatus(t("createCertificate.success.fieldApplied", { field: fieldLabels[field] }));
  };

  const exportTableToExcel = () => {
    try {
      // Filter out empty rows
      const nonEmptyRows = rows.filter(row => 
        Object.values(row).some(value => value && value.toString().trim() !== "")
      );

      if (nonEmptyRows.length === 0) {
        setStatus(t("createCertificate.error.noDataToExport"));
        return;
      }

      // Get current required fields for the selected certificate type
      const currentRequiredFields = requiredFields[certificateType] || [];
      const currentOptionalFields = optionalFields[certificateType] || [];
      const allFields = [...currentRequiredFields, ...currentOptionalFields];

      // Create worksheet data with headers
      const worksheetData = [];
      
      // Add headers row
      const headers = allFields.map(field => fieldLabels[field] || field);
      worksheetData.push(headers);

      // Add data rows
      nonEmptyRows.forEach(row => {
        const rowData = allFields.map(field => row[field] || "");
        worksheetData.push(rowData);
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Certificate Data");

      // Generate filename with simple date format
      const today = new Date();
      const day = today.getDate().toString().padStart(2, '0');
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const year = today.getFullYear().toString().slice(-2); // Last 2 digits of year
      const filename = `list_${certificateType || 'export'}_${day}${month}${year}.xlsx`;

      // Export file
      XLSX.writeFile(workbook, filename);
      
      setStatus(t("createCertificate.success.tableExported", { filename }));
    } catch (error) {
      console.error("Error exporting table:", error);
      setStatus(t("createCertificate.error.exportFailed"));
    }
  };

  const addRow = () => {
    setRows([...rows, {}]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
    const updatedErrors = errors.filter((error) => error.row !== index);
    setErrors(updatedErrors);
  };

  const resetTable = () => {
    setRows([{}]);
    setErrors([]);
    setStatus("");
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
        const sheetData = [];
        for (let row = range.s.r; row <= range.e.r; row++) {
          const rowData = [];
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            rowData.push(excelCellToValue(worksheet[cellAddress]));
          }
          sheetData.push(rowData);
        }

        let headerRowIndex = -1;
        for (let i = 0; i < sheetData.length; i++) {
          const row = sheetData[i];
          const normalizedRow = row.map((cell) => normalizeText(cell));
          const hasKnownColumn = normalizedRow.some((cell) =>
            Object.values(columnAliases).some((aliases) =>
              aliases.includes(cell)
            )
          );
          if (hasKnownColumn) {
            headerRowIndex = i;
            break;
          }
        }

        if (headerRowIndex === -1) {
          setStatus(t("createCertificate.errors.noHeadersFound"));
          setRows([{}]);
          return;
        }

        const headers = sheetData[headerRowIndex].map((header) => normalizeText(header));

        const hasVorname = headers.some((header) =>
          columnAliases.vorname.includes(header)
        );
        const hasNachname = headers.some((header) =>
          columnAliases.nachname.includes(header) && header !== "name"
        );
        const hasLastname = headers.some((header) =>
          ["lastname", "last-name", "last name", "surname"].includes(header)
        );

        const activeFields = [
          ...(requiredFields[certificateType] || []),
          ...(optionalFields[certificateType] || []),
        ];

        const fieldMapping = {};
        headers.forEach((header, idx) => {
          // Kursdatum is a soft fill only — never overwrite Startdatum / Enddatum columns
          if (["kursdatum", "coursedate", "trainingdate"].includes(header)) {
            fieldMapping[idx] = "_kursdatum";
            return;
          }
          if (hasVorname && header === "name" && !hasNachname) {
            fieldMapping[idx] = "nachname";
          } else if (hasLastname && header === "name" && !hasVorname) {
            fieldMapping[idx] = "vorname";
          } else {
            const matches = Object.entries(columnAliases)
              .filter(([, aliases]) => aliases.includes(header))
              .map(([field]) => field);
            if (matches.length === 1) {
              fieldMapping[idx] = matches[0];
            } else if (matches.length > 1) {
              fieldMapping[idx] =
                matches.find((field) => activeFields.includes(field)) || matches[0];
            }
          }
        });

        const dataRows = sheetData.slice(headerRowIndex + 1);
        const normalizedData = [];
        let skippedRows = 0;

        dataRows.forEach((row) => {
          const normalizedRow = {};
          row.forEach((cell, idx) => {
            if (fieldMapping[idx]) {
              const field = fieldMapping[idx];
              let value = cell == null || cell === "" ? "" : cell;
              if (dateFields.includes(field)) {
                value = convertDateToDDMMYYYY(value);
              } else if (typeof value !== "string") {
                value = String(value).trim();
              } else {
                value = value.trim();
              }
              if (field === "geschlecht") {
                value = normalizeGenderValue(value);
              }
              normalizedRow[field] = value;
            }
          });

          // Soft-fill from Kursdatum only into empty Start / End-Exam (never Ausgestellt am)
          const kursdatum = normalizedRow._kursdatum
            ? convertDateToDDMMYYYY(normalizedRow._kursdatum)
            : "";
          delete normalizedRow._kursdatum;
          if (kursdatum) {
            if (!normalizedRow.startdatum || String(normalizedRow.startdatum).trim() === "") {
              normalizedRow.startdatum = kursdatum;
            }
            if (
              activeFields.includes("endPrufungsdatum") &&
              (!normalizedRow.endPrufungsdatum || String(normalizedRow.endPrufungsdatum).trim() === "")
            ) {
              normalizedRow.endPrufungsdatum = kursdatum;
            }
            if (
              activeFields.includes("enddatum") &&
              (!normalizedRow.enddatum || String(normalizedRow.enddatum).trim() === "")
            ) {
              normalizedRow.enddatum = kursdatum;
            }
            if (
              activeFields.includes("prufungsdatum") &&
              (!normalizedRow.prufungsdatum || String(normalizedRow.prufungsdatum).trim() === "")
            ) {
              normalizedRow.prufungsdatum = kursdatum;
            }
          }

          // Keep combined End/Exam value mirrored for backend export
          if (normalizedRow.endPrufungsdatum) {
            normalizedRow.enddatum = normalizedRow.endPrufungsdatum;
            normalizedRow.prufungsdatum = normalizedRow.endPrufungsdatum;
          }

          // ASI: default Gültig bis = issue date + 6 years when not in the file
          if (
            certificateType === "Sachkundenachweis TRGS 519" &&
            normalizedRow.ausgestelltAm &&
            (!normalizedRow.gultigBis || String(normalizedRow.gultigBis).trim() === "")
          ) {
            normalizedRow.gultigBis = calculateValidUntil(
              normalizedRow.ausgestelltAm,
              certificateType
            );
          }

          const hasVorname = normalizedRow.vorname && isValidName(normalizedRow.vorname);
          const hasNachname = normalizedRow.nachname && isValidName(normalizedRow.nachname);
          const hasRequiredData = hasVorname && hasNachname;

          if (hasRequiredData) {
            const currentFields = requiredFields[certificateType] || [];
            const optFields = optionalFields[certificateType] || [];
            [...currentFields, ...optFields].forEach((field) => {
              if (!(field in normalizedRow)) {
                normalizedRow[field] = "";
              }
            });
            normalizedData.push(normalizedRow);
          } else {
            skippedRows++;
          }
        });

        if (normalizedData.length === 0) {
          setRows([{}]);
          setStatus(t("createCertificate.errors.noValidRows"));
          return;
        }

        setRows(normalizedData);
        validateRows(normalizedData);
        setStatus(
          t("createCertificate.success.fileUploaded") +
            (skippedRows > 0 ? ` (${skippedRows} ${t("createCertificate.messages.skippedRows")})` : "")
        );
      } catch (error) {
        setStatus(t("createCertificate.errors.fileUploadFailed"));
        console.error("Error reading file:", error);
      }
    };
    reader.onerror = () => {
      setStatus(t("createCertificate.errors.fileUploadFailed"));
    };
    reader.readAsArrayBuffer(uploadedFile);
  };

  const validateRows = (data = rows) => {
    const currentRequiredFields = requiredFields[certificateType] || [];
    const newErrors = [];

    data.forEach((row, index) => {
      currentRequiredFields.forEach((field) => {
        if (!row[field] || row[field].trim() === "") {
          newErrors.push({ row: index, field });
        }
      });
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleGenerate = async () => {
    if (!validateRows()) {
      setStatus(t("createCertificate.errors.requiredFields"));
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setStatus(t("createCertificate.errors.sessionExpired"));
      navigate("/");
      return;
    }

    const formData = new FormData();

    try {
      // Define explicit headers matching backend column_mapping
      const headers = {};
      const currentRequiredFields = requiredFields[certificateType] || [];
      const currentOptionalFields = optionalFields[certificateType] || [];
      const currentFields = [...currentRequiredFields, ...currentOptionalFields];
      currentFields.forEach(field => {
        switch (field) {
          case "geschlecht":
            headers[field] = "geschlecht";
            break;
          case "vorname":
            headers[field] = "vorname";
            break;
          case "nachname":
            headers[field] = "nachname";
            break;
          case "gebDatum":
            headers[field] = "geb.-datum";
            break;
          case "startdatum":
            headers[field] = "startdatum";
            break;
          case "enddatum":
            headers[field] = "enddatum";
            break;
          case "prufungsdatum":
            headers[field] = "prüfungsdatum";
            break;
          case "endPrufungsdatum":
            // Expanded to enddatum + prüfungsdatum when mapping rows
            headers[field] = "__endPrufungsdatum__";
            break;
          case "prufungsort":
            headers[field] = "prüfungsort";
            break;
          case "gultigBis":
            headers[field] = "gültig bis";
            break;
          case "ausgestelltAm":
            headers[field] = "ausgestellt am";
            break;
          case "prufer":
            headers[field] = "prüfer";
            break;
          case "teilnehmerId":
            // Ensure TN ID is sent using backend-expected key
            headers[field] = "teilnehmer_id";
            break;
          case "ersatzDatum":
            headers[field] = "ersatzdatum";
            break;
          case "geburtsort":
            headers[field] = "geburtsort";
            break;
          case "bescheidDatum":
            headers[field] = "bescheid_datum";
            break;
          case "aktenzeichen":
            headers[field] = "aktenzeichen";
            break;
          case "teilnehmerNr":
            headers[field] = "zertifikatsnr";
            break;
          default:
            break;
        }
      });

      // Map rows to match headers
      // For optional fields, only include them if they have a value
      const mappedRows = rows.map(row => {
        const mappedRow = {};
        currentFields.forEach(field => {
          const isOptional = currentOptionalFields.includes(field);
          const value = row[field];

          if (field === "endPrufungsdatum") {
            const shared = value || row.enddatum || row.prufungsdatum || "";
            mappedRow["enddatum"] = shared;
            mappedRow["prüfungsdatum"] = shared;
            return;
          }
          
          if (isOptional) {
            // For optional fields, only include if they have a non-empty value
            if (value && value.toString().trim() !== "") {
              mappedRow[headers[field]] = value;
            }
            // If empty, don't include the field at all
          } else {
            // Required fields always included (with empty string if missing)
            mappedRow[headers[field]] = value || "";
          }
        });
        return mappedRow;
      });

      // Debug: Log the number of rows and the data
      console.log("Number of Rows to Process:", mappedRows.length);
      console.log("Mapped Rows for Excel:", mappedRows);

      // Filter headers to only include optional fields that have at least one non-empty value
      const excelHeaderOrder = [];
      Object.keys(headers).forEach(field => {
        if (field === "endPrufungsdatum") {
          excelHeaderOrder.push("enddatum", "prüfungsdatum");
          return;
        }
        const isOptional = currentOptionalFields.includes(field);
        if (isOptional) {
          const headerKey = headers[field];
          const hasValue = mappedRows.some(row =>
            row[headerKey] !== undefined && row[headerKey] !== null && row[headerKey].toString().trim() !== ""
          );
          if (hasValue) excelHeaderOrder.push(headerKey);
        } else if (headers[field]) {
          excelHeaderOrder.push(headers[field]);
        }
      });

      // Create Excel file with explicit headers (only including optional fields that have values)
      const ws = XLSX.utils.json_to_sheet(mappedRows, { header: excelHeaderOrder });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Certificates");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const excelFile = new Blob([excelBuffer], { type: "application/octet-stream" });

      formData.append("file", excelFile, "certificates.xlsx");
      formData.append("certificateType", certificateType);
      formData.append("printableVersion", isPrintableVersion.toString());
      formData.append("organization", "Auraplan Academy");
      formData.append("includeBarcode", includeBarcode.toString());

      // Debug: Log the FormData entries
      for (let pair of formData.entries()) {
        console.log("FormData Entry:", pair[0], pair[1]);
      }

      setIsGenerating(true);

      const response = await axios.post(`${Config.API_BASE_URL}/generate`, formData, {
        headers: {
          "x-access-token": token,
        },
        responseType: "blob",
      });

      // Dynamic filename based on certificate type and date
      const formattedDate = getDownloadDateDdmmyy(certificateType, rows, requiredFields);
      const typeSlug = getDownloadTypeSlug(certificateType);
      const fileName =
        certificateType === "Namensschild"
          ? `namensschild_${formattedDate}.pdf`
          : `Auraplan_${typeSlug}_${formattedDate}.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setStatus(t("createCertificate.success.generated"));
    } catch (error) {
      console.error("Error generating certificate:", error);
      if (error.response?.status === 401) {
        setStatus(t("createCertificate.errors.sessionExpired"));
        navigate("/");
      } else if (error.response?.status === 400) {
        const errorText = await error.response.data.text();
        setStatus(errorText || t("createCertificate.errors.generationFailed"));
      } else if (error.message.includes("Network Error")) {
        setStatus(t("createCertificate.errors.internetIssue"));
      } else {
        setStatus(t("createCertificate.errors.serverErrorAdmin"));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const currentRequiredFields = requiredFields[certificateType] || [];
  const currentOptionalFields = optionalFields[certificateType] || [];
  const currentFields = [...currentRequiredFields, ...currentOptionalFields];
  const fieldsWithApplyLink = currentFields.filter(
    (field) => !["vorname", "nachname", "gebDatum"].includes(field)
  );

  return (
    <div className="create-certificate-container">
      <header className="create-certificate-header">
        <h1>{t("app.title")}</h1>
      </header>
      <main className="create-certificate-main">
        <section className="certificate-type-section animate-fade-in">
          {!certificateType ? (
            <div className="certificate-type-prompt">
              <h2>{t("createCertificate.prompt.selectCertificate")}</h2>
              <div className="form-group">
                <label htmlFor="certificateType">{t("createCertificate.labels.certificateType")}</label>
                <select
                  id="certificateType"
                  value={certificateType}
                  onChange={handleCertificateTypeChange}
                  className="form-input form-select"
                >
                  <option value="" disabled>{t("createCertificate.placeholders.selectCertificate")}</option>
                  {certificateTypes.map((type) => (
                    <option key={type} value={type}>
                      {t(`createCertificate.certificateTypes.${type}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="controls-container">
                <div className="certificate-type-group">
                  <h2>{t("createCertificate.sections.certificateType")}</h2>
                  <div className="form-group">
                    <label htmlFor="certificateType">{t("createCertificate.labels.certificateType")}</label>
                    <select
                      id="certificateType"
                      value={certificateType}
                      onChange={handleCertificateTypeChange}
                      className="form-input form-select"
                    >
                      <option value="" disabled>{t("createCertificate.placeholders.selectCertificate")}</option>
                      {certificateTypes.map((type) => (
                        <option key={type} value={type}>
                          {t(`createCertificate.certificateTypes.${type}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {status && <div className="status-toast">{status}</div>}

              {errors.length > 0 && (
                <div className="error-message">
                  {t("createCertificate.errors.requiredFields")}
                </div>
              )}

              <div className="table-section">
                <div className="excel-upload-group">
                  <h3>{t("createCertificate.sections.excelUpload")}</h3>
                  <div className="form-group">
                    <label htmlFor="fileInput">{t("createCertificate.labels.uploadFile")}</label>
                    <div className="upload-wrapper" onClick={() => fileInputRef.current?.click()}>
                      <input
                        type="file"
                        id="fileInput"
                        accept=".xls,.xlsx,.csv"
                        onChange={handleFileChange}
                        className="upload-input"
                        ref={fileInputRef}
                      />
                      <Upload size={20} className="upload-icon" />
                      <span className="upload-text">{t("createCertificate.labels.uploadPrompt")}</span>
                    </div>
                    <p className="upload-hint">{t("createCertificate.labels.uploadHint")}</p>
                  </div>
                </div>

                <div className="data-table-wrapper">
                  {rows.some(row => Object.keys(row).some(key => row[key] && row[key].trim() !== "")) && (
                    <div className="table-controls">
                      <button className="export-table-btn" onClick={exportTableToExcel} title={t("createCertificate.buttons.exportTable")}>
                        <FileText size={16} />
                        {t("createCertificate.buttons.exportTable")}
                      </button>
                      <button className="reset-table-btn" onClick={resetTable} title={t("createCertificate.buttons.resetTable")}>
                        <X size={16} />
                        {t("createCertificate.buttons.resetTable")}
                      </button>
                    </div>
                  )}
                  <table className="data-table">
                    <thead>
                      <tr>
                        {currentFields.map((field) => (
                          <th key={field} className={`table-header ${fieldLabels[field].length > 15 ? "long-text" : ""}`}>
                            <div className="header-content">
                              <span>{fieldLabels[field]}</span>
                              {field === "gultigBis" && getValidityInfo(certificateType) && (
                                <button
                                  className="info-button"
                                  onClick={() => setShowValidityInfo(true)}
                                  title="Validity period information"
                                >
                                  <Info size={14} />
                                </button>
                              )}
                            </div>
                          </th>
                        ))}
                        <th className="table-header actions-column">{t("createCertificate.labels.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index} className="table-row">
                          {currentFields.map((field) => (
                            <td key={field} className="table-cell">
                              <div className="table-input-container">
                                {field === "geschlecht" ? (
                                  <select
                                    value={row[field] || ""}
                                    onChange={(e) => handleInputChange(index, field, e.target.value)}
                                    className={`table-input table-select ${errors.some((error) => error.row === index && error.field === field) ? "input-error" : ""}`}
                                  >
                                    <option value="">{t(`createCertificate.placeholders.${field}`)}</option>
                                    <option value="Herr">{t("createCertificate.options.herr")}</option>
                                    <option value="Frau">{t("createCertificate.options.frau")}</option>
                                    <option value="Divers">{t("createCertificate.options.divers")}</option>
                                  </select>
                                ) : field === "ersatzDatum" && certificateType === "Sifa LEK 1-5" ? (
                                  <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                    <input
                                      type="text"
                                      value={row[field] || ""}
                                      onChange={(e) => handleInputChange(index, field, e.target.value)}
                                      className={`table-input ${errors.some((error) => error.row === index && error.field === field) ? "input-error" : ""}`}
                                      placeholder={t(`createCertificate.placeholders.${field}`)}
                                    />
                                    <span style={{fontSize: '10px', color: '#666', fontStyle: 'italic'}}>
                                      {t("createCertificate.labels.optionalSifa15")}
                                    </span>
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    value={row[field] || ""}
                                    onChange={(e) => handleInputChange(index, field, e.target.value)}
                                    className={`table-input ${errors.some((error) => error.row === index && error.field === field) ? "input-error" : ""}`}
                                    placeholder={t(`createCertificate.placeholders.${field}`)}
                                  />
                                )}
                                {index === 0 && fieldsWithApplyLink.includes(field) && (
                                  <button
                                    className="apply-to-all-btn"
                                    onClick={() => applyFieldToAllRows(field, row[field] || "")}
                                    title={t("createCertificate.buttons.applyToAllRows", { field: fieldLabels[field] })}
                                  >
                                    {t("createCertificate.buttons.applyToAllRowsStatic")}
                                  </button>
                                )}
                              </div>
                            </td>
                          ))}
                          <td className="table-cell actions-column">
                            <button
                              className="remove-row-btn"
                              onClick={() => removeRow(index)}
                              disabled={rows.length === 1}
                              title={t("createCertificate.buttons.removeRow")}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button className="add-row-btn" onClick={addRow} title={t("createCertificate.buttons.addRow")}>
                    <Plus size={20} className="btn-icon" />
                    {t("createCertificate.buttons.addRow")}
                  </button>
                </div>

                {/* Digital/Printable Toggle */}
                <div className="form-group toggle-group">
                  <label className="toggle-label">{t("createCertificate.labels.versionQuestion")}</label>
                  <p className="toggle-description">{t("createCertificate.labels.versionDescription")}</p>
                  <ToggleButtonGroup
                    className="custom-toggle-group"
                    value={isPrintableVersion ? "Printable" : "Digital"}
                    exclusive
                    onChange={(event, newVersion) => {
                      if (newVersion !== null) {
                        setIsPrintableVersion(newVersion === "Printable");
                      }
                    }}
                    aria-label="Version"
                  >
                    <ToggleButton value="Digital" aria-label="Digital">
                      {t("createCertificate.labels.digitalVersion")}
                    </ToggleButton>
                    <ToggleButton value="Printable" aria-label="Printable">
                      {t("createCertificate.labels.printableVersion")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>

                {/* Blockchain/Barcode Toggle */}
                <div className="form-group toggle-group">
                  <label className="toggle-label">{t("createCertificate.labels.blockchainQuestion")}</label>
                  <p className="toggle-description">{t("createCertificate.labels.blockchainDescription")}</p>
                  <ToggleButtonGroup
                    className="custom-toggle-group"
                    value={includeBarcode ? "Yes" : "No"}
                    exclusive
                    onChange={(event, newValue) => {
                      if (newValue !== null) {
                        setIncludeBarcode(newValue === "Yes");
                      }
                    }}
                    aria-label="Blockchain and Barcode"
                  >
                    <ToggleButton value="No" aria-label="No">
                      {t("createCertificate.labels.no")}
                    </ToggleButton>
                    <ToggleButton value="Yes" aria-label="Yes">
                      {t("createCertificate.labels.yes")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>
            </>
          )}
        </section>

        {certificateType && (
          <section className="actions-section animate-fade-in">
            <button
              className="generate-btn"
              onClick={handleGenerate}
              title={t("createCertificate.buttons.generate")}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader size={20} className="btn-icon spin" />
                  {loadingMessage || t("createCertificate.buttons.generating")}
                </>
              ) : (
                <>
                  <Download size={20} className="btn-icon" />
                  {t("createCertificate.buttons.generate")}
                </>
              )}
            </button>
            <button
              className="cancel-btn"
              onClick={() => navigate("/dashboard")}
              title={t("createCertificate.buttons.cancel")}
              disabled={isGenerating}
            >
              <X size={20} className="btn-icon" />
              {t("createCertificate.buttons.cancel")}
            </button>
          </section>
        )}
      </main>

      {/* Validity Information Modal */}
      {showValidityInfo && (
        <div className="modal-overlay" onClick={() => setShowValidityInfo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Certificate Validity Information</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowValidityInfo(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {getValidityInfo(certificateType) && (
                <div className="validity-info">
                  <p><strong>Certificate Type:</strong> {certificateType}</p>
                  <p><strong>Validity Period:</strong> {getValidityInfo(certificateType).period}</p>
                  <p><strong>Note:</strong> {getValidityInfo(certificateType).message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCertificate;